import * as Sentry from '@sentry/react';

import { AcquisitionOperation } from '../../../services/api/acquisition/acquisitionOperation';
import { AcquisitionOperationProduct } from '../../../services/api/acquisition/acquisitionRecovery';
import { getTassoQuintoErrorCode } from '../../../services/api/tasso-quinto';
import { useUserStore } from '../../../store/user/useUserStore';
import { isExpectedAcquisitionRefusal } from './acquisitionErrors';
import { ACQUISITION_STEPS } from './acquisitionOutcome';

/**
 * Everything a failed acquisition may be reported with, and nothing else.
 *
 * Support investigates an acquisition from its identifiers: which operation,
 * which request, which real operator, which product, which step stopped and
 * which stable code says why. None of that describes the customer, and the
 * command that does never leaves the browser — not in an extra, not inside an
 * error object, not as a serialized payload. The context is therefore built
 * field by field from a fixed list rather than spread from whatever the caller
 * happens to hold.
 */
export interface AcquisitionDiagnosticContext {
  product: AcquisitionOperationProduct;
  operationId: string | null;
  requestId: string | null;
  /** The signed-in admin, never the impersonated owner: the acting identity. */
  actorId: string | null;
  failedStep: string | null;
  code: string | null;
}

interface ErrorEnvelope {
  requestId?: unknown;
  code?: unknown;
}

const responseDataOf = (error: unknown): ErrorEnvelope => {
  const response = (error as { response?: { data?: unknown } })?.response;
  const data = response?.data;
  return data && typeof data === 'object' ? (data as ErrorEnvelope) : {};
};

const requestIdOf = (error: unknown): string | null => {
  const requestId = responseDataOf(error).requestId;
  return typeof requestId === 'string' ? requestId : null;
};

/**
 * The first step the backend did not complete. It is where the workflow
 * stopped, which is what a code alone cannot say: two operations carrying
 * `hubspot_timeout` are different problems when one still has its lead.
 */
const failedStepOf = (operation: AcquisitionOperation): string | null =>
  ACQUISITION_STEPS.find((step) => operation.steps[step] !== 'completed') ??
  null;

/**
 * The real actor. Impersonation lives in local storage and never replaces the
 * signed-in user, so reading the user store is what makes this the operator who
 * actually sent the command rather than the owner they were looking at.
 */
const actorIdOf = (): string | null =>
  useUserStore.getState().user?.id ?? null;

const report = (message: string, context: AcquisitionDiagnosticContext) => {
  Sentry.captureMessage(message, {
    level: 'error',
    // Written under one key, as one flat object of known strings: an `extra`
    // assembled from several sources is how a payload gets in by accident.
    extra: { acquisition: context },
  });
};

/**
 * Durable work that did not finish. It is a domain result rather than an
 * exception, so it is reported as a message with the operation's own
 * identifiers: the backend recorded something the operator may have to resolve,
 * and support has to be able to find it.
 */
export const reportIncompleteAcquisition = (
  product: AcquisitionOperationProduct,
  operation: AcquisitionOperation
) =>
  report('Acquisition operation incomplete', {
    product,
    operationId: operation.operationId,
    requestId: operation.requestId,
    actorId: actorIdOf(),
    failedStep: failedStepOf(operation),
    code: operation.code ?? null,
  });

/**
 * A refused or failed call. A refusal that is the workflow working — an
 * ineligible profile, a held lease, a session to renew — is nobody's defect, so
 * only what was not expected is reported, and only by its code.
 */
export const reportAcquisitionFailure = (
  product: AcquisitionOperationProduct,
  error: unknown,
  operation: AcquisitionOperation | null = null
) => {
  if (isExpectedAcquisitionRefusal(error)) return;

  report('Acquisition request failed', {
    product,
    operationId: operation?.operationId ?? null,
    requestId: requestIdOf(error) ?? operation?.requestId ?? null,
    actorId: actorIdOf(),
    failedStep: operation ? failedStepOf(operation) : null,
    code: getTassoQuintoErrorCode(error),
  });
};
